import React from 'react';

/**
 * Customer-facing error boundary. Wraps the customer portal so a single
 * component crash falls back to a friendly "something went wrong" screen
 * instead of a blank page. Logs the error to the console so support can
 * still diagnose if a customer reports it.
 */
export class PortalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('Portal error boundary caught:', error, info);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleSignOut = () => {
    try {
      localStorage.removeItem('portal_token');
      localStorage.removeItem('portal_customer');
    } catch {
      // ignore — localStorage may be disabled
    }
    window.location.href = '/portal/login';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)' }}
        data-testid="portal-error-boundary"
      >
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold text-slate-900 mb-2">Something went wrong</h1>
          <p className="text-slate-600 mb-6">
            We hit an unexpected error loading this page. Please try refreshing — if the problem keeps happening, get in touch with us at{' '}
            <a href="mailto:hello@helioses.co.uk" className="text-blue-600 underline">hello@helioses.co.uk</a>.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={this.handleReload}
              className="px-5 py-2.5 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors"
              data-testid="portal-error-reload"
            >
              Refresh page
            </button>
            <button
              onClick={this.handleSignOut}
              className="px-5 py-2.5 rounded-lg border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 transition-colors"
              data-testid="portal-error-signout"
            >
              Sign out
            </button>
          </div>
          {this.state.error && (
            <details className="mt-6 text-left text-xs text-slate-400">
              <summary className="cursor-pointer">Technical details</summary>
              <pre className="mt-2 whitespace-pre-wrap break-all">{String(this.state.error?.message || this.state.error)}</pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}

export default PortalErrorBoundary;
